import React, { useMemo } from 'react';
import { Text, Group } from 'react-konva';

import FlashbackLayout from 'common/components/RewindModal/Konvas/FlashbackLayout';
import KonvaImage from 'common/components/RewindModal/Konvas/KonvaImage';
import ShareableStage from 'common/components/RewindModal/Konvas/ShareableStage';
import {
  LAYER_WIDTH,
  LAYER_HEIGHT,
  BLOCK_MARGIN,
  FLASHBACK_COVER_LOGO_WIDTH,
  FLASHBACK_COVER_LOGO_HEIGHT,
  FLASHBACK_COVER_LOGO_TOP_MARGIN,
  FLASHBACK_COVER_LOGO_OPACITY,
  ALLOCINE_LOGO_WIDTH,
  ALLOCINE_LOGO_HEIGHT,
  ALLOCINE_LOGO_BOTTOM_MARGIN,
  SLIDE_CARD_WIDTH,
  SLIDE_CARD_HEIGHT,
  SLIDE_CARD_CORNER_RADIUS,
  FLASHBACK_BACKGROUND_COLOR,
  FLASHBACK_TEXT_COLOR,
  FLASHBACK_GOLDEN_GRADIENT,
  FLASHBACK_FONT_FAMILY
} from 'common/components/RewindModal/Konvas/constants';
import { getDPRSize } from 'common/components/RewindModal/helper';
import { IMG_YELLOW_STAR } from 'common/configuration/constants';

const styleConfig = {
  ratingFontSize: getDPRSize(100),
  ratingFontStyle: '800',
  ratingLineHeight: 0.83,

  titleFontSize: getDPRSize(20),
  titleFontStyle: '700',
  titleLineHeight: 1,

  starFontSize: getDPRSize(38)
};

const topCardTransforms = [
  { xOffset: -getDPRSize(120), yOffset: getDPRSize(10), rotation: -10 },
  { xOffset: getDPRSize(0), yOffset: -getDPRSize(10), rotation: 5 },
  { xOffset: getDPRSize(120), yOffset: getDPRSize(15), rotation: 20 }
];

const bottomCardTransforms = [
  { xOffset: -getDPRSize(120), yOffset: getDPRSize(10), rotation: 10 },
  { xOffset: getDPRSize(0), yOffset: getDPRSize(20), rotation: -5 },
  { xOffset: getDPRSize(120), yOffset: getDPRSize(10), rotation: 15 }
];

type ScreenAverageRatingProps = {
  data: {
    average_rating: number;
    average_rating_phrase_1?: string;
    average_rating_phrase_2?: string;
    average_rating_wording?: string;
  };
  entities: any[];
};

const ShareableScreenAverageRating = React.forwardRef<
  any,
  ScreenAverageRatingProps
>(({ data, entities }, ref) => {
  const hasWording = !!data?.average_rating_wording;
  const hasPhrases =
    !!data?.average_rating_phrase_1 && !!data?.average_rating_phrase_2;

  const ratedEntities = useMemo(
    () => entities?.[0]?.entities ?? [],
    [entities]
  );

  const shouldShowCovers = useMemo(
    () => ratedEntities.length >= 3,
    [ratedEntities.length]
  );

  const topCardsData = useMemo(() => {
    const length = ratedEntities.length;
    if (length === 3) {
      return ratedEntities.slice(0, 2);
    } else if (length === 4) {
      return ratedEntities.slice(0, 2);
    } else if (length === 5) {
      return ratedEntities.slice(0, 2);
    }
    return ratedEntities.slice(0, 3);
  }, [ratedEntities]);

  const bottomCardsData = useMemo(() => {
    const length = ratedEntities.length;
    if (length === 3) {
      return ratedEntities.slice(2, 3);
    } else if (length === 4) {
      return ratedEntities.slice(2, 4);
    } else if (length === 5) {
      return ratedEntities.slice(2, 5);
    }
    return ratedEntities.slice(3, 6);
  }, [ratedEntities]);

  const textLayout = useMemo(() => {
    const spacing_star_to_rating = BLOCK_MARGIN;
    const spacing_rating_to_phrase1 = BLOCK_MARGIN * 0.5;
    const spacing_phrase1_to_phrase2 = BLOCK_MARGIN * 0.5;

    const starHeight = styleConfig.starFontSize;
    const ratingHeight =
      styleConfig.ratingFontSize * styleConfig.ratingLineHeight;
    const title1Height =
      styleConfig.titleFontSize * styleConfig.titleLineHeight;
    const title2Height =
      styleConfig.titleFontSize * styleConfig.titleLineHeight;

    const y_star = 0;
    const y_rating = y_star + starHeight + spacing_star_to_rating;
    const y_phrase1 = y_rating + ratingHeight + spacing_rating_to_phrase1 * 3;
    const y_phrase2 = y_phrase1 + title1Height + spacing_phrase1_to_phrase2 * 5;
    const totalHeight = y_phrase2 + title2Height * 2;

    return { y_star, y_rating, y_phrase1, y_phrase2, totalHeight };
  }, []);

  const layout = useMemo(() => {
    const textBlockHeight = textLayout.totalHeight;

    if (shouldShowCovers) {
      const topCardsY =
        FLASHBACK_COVER_LOGO_TOP_MARGIN +
        FLASHBACK_COVER_LOGO_HEIGHT +
        BLOCK_MARGIN +
        SLIDE_CARD_HEIGHT / 2;
      const centerTextY = topCardsY + SLIDE_CARD_HEIGHT / 2;
      const bottomCardsY =
        centerTextY +
        textBlockHeight +
        BLOCK_MARGIN * 0.5 +
        SLIDE_CARD_HEIGHT / 2;

      return { topCardsY, centerTextY, bottomCardsY };
    } else {
      // Centrer le texte verticalement si pas de cartes
      const availableHeight =
        LAYER_HEIGHT -
        FLASHBACK_COVER_LOGO_TOP_MARGIN -
        FLASHBACK_COVER_LOGO_HEIGHT -
        ALLOCINE_LOGO_HEIGHT -
        ALLOCINE_LOGO_BOTTOM_MARGIN;
      const centerTextY =
        FLASHBACK_COVER_LOGO_TOP_MARGIN +
        FLASHBACK_COVER_LOGO_HEIGHT +
        (availableHeight - textBlockHeight) / 2;

      return { topCardsY: 0, centerTextY, bottomCardsY: 0 };
    }
  }, [textLayout, shouldShowCovers]);

  const centerX = LAYER_WIDTH / 2;

  return (
    <ShareableStage ref={ref}>
      <FlashbackLayout
        backgroundColor={FLASHBACK_BACKGROUND_COLOR}
        backgroundOpacity={1}
        coverLogoWidth={FLASHBACK_COVER_LOGO_WIDTH}
        coverLogoHeight={FLASHBACK_COVER_LOGO_HEIGHT}
        coverLogoTopMargin={FLASHBACK_COVER_LOGO_TOP_MARGIN}
        coverLogoOpacity={FLASHBACK_COVER_LOGO_OPACITY}
        allocineLogoWidth={ALLOCINE_LOGO_WIDTH}
        allocineLogoHeight={ALLOCINE_LOGO_HEIGHT}
        allocineLogoBottomMargin={ALLOCINE_LOGO_BOTTOM_MARGIN}
      >
        {shouldShowCovers && (
          <Group>
            {topCardsData.map((entity: any, index: number) => {
              const isThreeCoversCase = ratedEntities.length === 3;
              let transform = topCardTransforms[index] || {
                xOffset: 0,
                yOffset: 0,
                rotation: 0
              };

              // Si 3 covers, ajuster les 2 covers du haut
              if (isThreeCoversCase) {
                if (index === 0) {
                  // Première cover (A) : décaler vers la droite
                  transform = {
                    ...transform,
                    xOffset: transform.xOffset + getDPRSize(60)
                  };
                } else if (index === 1) {
                  // Deuxième cover (B) : même hauteur que A, rotation inverse
                  transform = {
                    xOffset:
                      topCardTransforms[0].xOffset +
                      getDPRSize(60) +
                      getDPRSize(120),
                    yOffset: topCardTransforms[0].yOffset,
                    rotation: -topCardTransforms[0].rotation
                  };
                }
              }

              return (
                <KonvaImage
                  key={`top-${entity.id}`}
                  path={entity.poster?.path}
                  x={centerX + transform.xOffset}
                  y={layout.topCardsY + transform.yOffset}
                  rotation={transform.rotation}
                  offsetX={SLIDE_CARD_WIDTH / 2}
                  offsetY={SLIDE_CARD_HEIGHT / 2}
                  width={SLIDE_CARD_WIDTH}
                  height={SLIDE_CARD_HEIGHT}
                  cornerRadius={SLIDE_CARD_CORNER_RADIUS}
                />
              );
            })}
          </Group>
        )}

        <Group y={layout.centerTextY}>
          <KonvaImage
            path={IMG_YELLOW_STAR}
            isLocal={true}
            width={styleConfig.starFontSize}
            height={styleConfig.starFontSize}
            x={(LAYER_WIDTH - styleConfig.starFontSize) / 2}
            y={textLayout.y_star}
          />
          <Text
            text={data?.average_rating?.toFixed(1) ?? 'N/A'}
            fontSize={styleConfig.ratingFontSize}
            fontFamily={FLASHBACK_FONT_FAMILY}
            fontStyle={styleConfig.ratingFontStyle}
            lineHeight={styleConfig.ratingLineHeight}
            width={LAYER_WIDTH}
            align="center"
            y={textLayout.y_rating}
            fillLinearGradientColorStops={FLASHBACK_GOLDEN_GRADIENT}
            fillLinearGradientStartPoint={{ x: 0, y: 0 }}
            fillLinearGradientEndPoint={{ x: LAYER_WIDTH, y: 0 }}
          />
          {hasWording ? (
            <Text
              text={data?.average_rating_wording}
              fontSize={styleConfig.titleFontSize}
              fontFamily={FLASHBACK_FONT_FAMILY}
              fontStyle={styleConfig.titleFontStyle}
              lineHeight={styleConfig.titleLineHeight}
              fill={FLASHBACK_TEXT_COLOR}
              width={LAYER_WIDTH - BLOCK_MARGIN * 4}
              x={BLOCK_MARGIN * 2}
              align="center"
              y={textLayout.y_phrase1}
            />
          ) : hasPhrases ? (
            <>
              <Text
                text={data?.average_rating_phrase_1}
                fontSize={styleConfig.titleFontSize}
                fontFamily={FLASHBACK_FONT_FAMILY}
                fontStyle={styleConfig.titleFontStyle}
                lineHeight={styleConfig.titleLineHeight}
                fill={FLASHBACK_TEXT_COLOR}
                width={LAYER_WIDTH - BLOCK_MARGIN * 4}
                x={BLOCK_MARGIN * 2}
                align="center"
                y={textLayout.y_phrase1}
              />
              <Text
                text={data?.average_rating_phrase_2}
                fontSize={getDPRSize(17)}
                fontFamily={FLASHBACK_FONT_FAMILY}
                fontStyle="500"
                lineHeight={styleConfig.titleLineHeight}
                fill={FLASHBACK_TEXT_COLOR}
                width={LAYER_WIDTH - BLOCK_MARGIN * 4}
                x={BLOCK_MARGIN * 2}
                align="center"
                y={textLayout.y_phrase2}
              />
            </>
          ) : null}
        </Group>

        {shouldShowCovers && (
          <Group>
            {bottomCardsData.map((entity: any, index: number) => {
              const isThreeCoversCase = ratedEntities.length === 3;
              let transform = bottomCardTransforms[index] || {
                xOffset: 0,
                yOffset: 0,
                rotation: 0
              };

              // Si 3 covers, centrer la cover du bas sur l'étoile
              if (isThreeCoversCase) {
                transform = {
                  ...transform,
                  xOffset: 0
                };
              }

              return (
                <KonvaImage
                  key={`bottom-${entity.id}`}
                  path={entity.poster?.path}
                  x={centerX + transform.xOffset}
                  y={layout.bottomCardsY + transform.yOffset}
                  rotation={transform.rotation}
                  offsetX={SLIDE_CARD_WIDTH / 2}
                  offsetY={SLIDE_CARD_HEIGHT / 2}
                  width={SLIDE_CARD_WIDTH}
                  height={SLIDE_CARD_HEIGHT}
                  cornerRadius={SLIDE_CARD_CORNER_RADIUS}
                />
              );
            })}
          </Group>
        )}
      </FlashbackLayout>
    </ShareableStage>
  );
});

ShareableScreenAverageRating.displayName = 'ShareableScreenAverageRating';
export default ShareableScreenAverageRating;
